import { useSyncExternalStore } from 'react'

export type ColorScheme = 'light' | 'dark'

function getSnapshot(): ColorScheme {
  return typeof document !== 'undefined' &&
    document.documentElement.classList.contains('dark')
    ? 'dark'
    : 'light'
}

// SSR / non-DOM environments have no theme to read; default to light.
function getServerSnapshot(): ColorScheme {
  return 'light'
}

function subscribe(onStoreChange: () => void): () => void {
  if (typeof document === 'undefined') return () => {}
  const observer = new MutationObserver(onStoreChange)
  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['class'],
  })
  return () => observer.disconnect()
}

/**
 * Resolved colour scheme, read from the `dark`/`light` class that
 * `PopcornProvider` toggles on `<html>` (it removes/adds these classes on every
 * theme transition, including OS-preference changes in 'system' mode).
 *
 * The charts resolve their colours imperatively from CSS custom properties via
 * `getComputedStyle` (see `resolveColor` in LineChart), which captures the
 * value *at render time* — it does not re-read when the theme later flips.
 * Worse, `PopcornProvider` applies the class change in an effect (after commit),
 * so there is no render in which the new class is already on `<html>`. This hook
 * closes that gap: the `MutationObserver` fires when the class actually changes,
 * which triggers a re-render in which `getComputedStyle` reads the new theme's
 * values.
 *
 * Reading the document class directly — rather than `useTheme` / `useIsDarkMode`
 * — is deliberate: those context values change *during* the toggle render,
 * before the class is applied (so a re-read would still see the old theme), and
 * they throw outside a `PopcornProvider` (e.g. the provider-less offscreen
 * render used by the chart-share snapshot). Observing the DOM avoids both.
 */
export function useDocumentColorScheme(): ColorScheme {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)
}
